import { useMemo, useState, useEffect, useCallback, lazy, Suspense } from 'react'
import { useParams, useNavigate, Link } from '@tanstack/react-router'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Button } from './ui/button'
import { Input } from './ui/input'
import { Label } from './ui/label'
import { Textarea } from './ui/textarea'
import { Checkbox } from './ui/checkbox'
import { Badge } from './ui/badge'
import { Alert, AlertDescription, AlertTitle } from './ui/alert'
import { useFormatters } from '../lib/format'
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs'
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from './ui/alert-dialog'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select'
import { mdiAlert, mdiBookOpenVariant, mdiChevronDown, mdiClose, mdiCodeTags, mdiContentSave, mdiFileTree, mdiFlaskOutline, mdiLoading, mdiPlus, mdiPound, mdiSwapHorizontal, mdiTrashCanOutline } from '@mdi/js'
import { AppIcon } from './ui/icon'
import { useTheme } from '../contexts/ThemeContext'
import { useCan } from '../contexts/PermissionsContext'

const CodeMirror = lazy(() => import('./derivations/ScriptEditor'))
import { useBreadcrumbs } from '../hooks/useBreadcrumbs'
import { useRouteTab } from '../hooks/useRouteTab'
import { $api } from '../api/queryClient'
import { useAssetTypes } from '../api/queries'
import { DerivationRulesTable } from './derivations/DerivationRulesTable'
import { DerivationTestPanel } from './derivations/DerivationTestPanel'
import { DerivationInputsField } from './derivations/DerivationInputsField'
import { AssetTypeTile, type AssetTypeLike } from './assets/AssetTypeTile'
import { TryOnAssetPanel } from './derivations/TryOnAssetPanel'
import { SCRIPT_API_REFERENCE } from './derivations/scriptReference'
import { sortInputs } from './derivations/inputs'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from './ui/collapsible'
import { usePropertyDefinitions } from '../hooks/useQueries'

function invalidateDerivation(queryClient: ReturnType<typeof useQueryClient>) {
  queryClient.invalidateQueries({
    predicate: (query) => {
      const queryKey = query.queryKey as any[]
      if (queryKey.length >= 2 && queryKey[0] === 'get') {
        const path = queryKey[1] as string
        return path.includes('/derivations')
      }
      return false
    }
  })
}

const DERIVATION_TABS = ['overview', 'rules', 'test'] as const

export function DerivationDetailPage() {
  const { org, uid } = useParams({ strict: false }) as { org: string; uid: string }
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { t } = useTranslation('derivations')
  const { formatDate } = useFormatters()
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  // Deep-linkable tab: `?tab=rules` opens the Rules tab directly (used by the
  // compliance tab's "edit the threshold" bridge), and switching tabs writes it
  // back so a reload keeps it. Falls back to overview.
  const [activeTab, setActiveTab] = useRouteTab(DERIVATION_TABS, 'overview')

  // Editable fields
  const [code, setCode] = useState('')
  const [nameEn, setNameEn] = useState('')
  const [nameFr, setNameFr] = useState('')
  const [description, setDescription] = useState('')
  const [targetPropertyCode, setTargetPropertyCode] = useState('')
  // Asset-type scope (spec 2026-09-02-02): codes the derivation applies to. Empty = every asset.
  const [assetTypeCodes, setAssetTypeCodes] = useState<string[]>([])
  const [enabled, setEnabled] = useState(true)
  const [script, setScript] = useState('')
  // The one input list (spec 2026-10-06-13). Editable for a script only: a
  // decision table computes its inputs from its conditions.
  const [inputs, setInputs] = useState<string[]>([])
  // Paths the last test run read without declaring them (spec 2026-10-06-15).
  const [undeclaredInputs, setUndeclaredInputs] = useState<string[]>([])
  const [showConvertDialog, setShowConvertDialog] = useState(false)

  const { theme } = useTheme()
  const { data: assetTypes = [] } = useAssetTypes(org)
  // Completion data for the script editor (spec 2026-10-06-17).
  const { data: propertyDefinitions } = usePropertyDefinitions(org)
  const completion = useMemo(() => ({
    propertyCodes: propertyDefinitions.map((p: any) => p.code as string).filter(Boolean).sort(),
    assetTypeCodes: (assetTypes as any[]).map((at) => at.code as string).filter(Boolean).sort(),
  }), [propertyDefinitions, assetTypes])
  // Derivations are part of the type system: `configuration.write` gates every
  // mutation here. Fields stay on screen read-only so a `configuration.read`
  // caller can still inspect the definition (spec 2026-09-04-02, "Gating").
  const canWriteConfiguration = useCan('configuration.write')

  useBreadcrumbs(useMemo(() => [
    { title: t('common:nav.home'), path: `/${org}` },
    { title: t('common:nav.settings'), path: `/${org}/settings` },
    { title: t('breadcrumb'), path: `/${org}/derivations` },
    { title: code || t('common:status.loading') }
  ], [org, code, t]))

  const { data: derivation, isLoading, error } = $api.useQuery('get', '/api/v1/organizations/{orgCode}/derivations/{derivationUid}', {
    params: { path: { orgCode: org, derivationUid: uid } }
  })

  const d = derivation as any
  const isTable = d?.kind === 'decision_table'

  useEffect(() => {
    if (d) {
      setCode(d.code || '')
      setNameEn(d.name?.en || '')
      setNameFr(d.name?.fr || '')
      setDescription(d.description || '')
      setTargetPropertyCode(d.target_property?.code || '')
      setAssetTypeCodes(d.asset_types || [])
      setEnabled(d.enabled ?? true)
      setScript(d.script || '')
      setInputs(d.inputs || [])
    }
  }, [d])

  const updateMutation = $api.useMutation('patch', '/api/v1/organizations/{orgCode}/derivations/{derivationUid}', {
    onSuccess: () => invalidateDerivation(queryClient)
  })

  const deleteMutation = $api.useMutation('delete', '/api/v1/organizations/{orgCode}/derivations/{derivationUid}', {
    onSuccess: () => {
      invalidateDerivation(queryClient)
      navigate({ to: '/$org/derivations', params: { org } })
    }
  })

  const handleSave = useCallback(() => {
    updateMutation.mutate({
      params: { path: { orgCode: org, derivationUid: uid } },
      body: {
        code,
        name: { en: nameEn, fr: nameFr },
        description: description || undefined,
        target_property_code: targetPropertyCode,
        asset_types: assetTypeCodes,
        enabled,
        // A table is edited on its Rules tab; its script is compiled, not sent.
        ...(isTable ? {} : { script, inputs }),
      } as any
    })
  }, [code, nameEn, nameFr, description, targetPropertyCode, assetTypeCodes, enabled, isTable, script, inputs, org, uid, updateMutation])

  // Inputs the script reads without declaring them, found by the server on
  // save (spec 2026-10-06-17). Accepting adds them and saves at once.
  const proposedInputs = ((d?.proposed_inputs || []) as string[]).filter((p) => !inputs.includes(p))
  const acceptProposedInputs = useCallback((paths: string[]) => {
    const next = sortInputs([...inputs, ...paths])
    setInputs(next)
    updateMutation.mutate({
      params: { path: { orgCode: org, derivationUid: uid } },
      body: { script, inputs: next } as any
    })
  }, [inputs, script, org, uid, updateMutation])

  // Convert to script: the table is dropped and its compiled script, with
  // the inputs computed from its conditions, becomes the derivation.
  const handleConvert = useCallback(() => {
    updateMutation.mutate({
      params: { path: { orgCode: org, derivationUid: uid } },
      body: { decision_table: null, script: d?.script || '', inputs: d?.inputs || [] } as any
    }, {
      onSuccess: () => {
        setShowConvertDialog(false)
        setActiveTab('overview')
      }
    })
  }, [org, uid, d, updateMutation, setActiveTab])

  const saveError = updateMutation.error as any
  const saveErrorMessage = saveError
    ? [saveError.title, ...((saveError.fields || []) as any[]).map((f: any) => f.message)].filter(Boolean).filter((m: string, i: number, all: string[]) => all.indexOf(m) === i).join(' — ')
    : ''

  const assetTypeLabel = useCallback((atCode: string) => {
    const at = assetTypes.find((a: any) => a.code === atCode) as any
    if (!at) return atCode
    const name = typeof at.name === 'string' ? at.name : at.name?.en || at.name?.fr || at.code
    return name
  }, [assetTypes])

  const handleToggleEnabled = useCallback((checked: boolean) => {
    setEnabled(checked)
    updateMutation.mutate({
      params: { path: { orgCode: org, derivationUid: uid } },
      body: { enabled: checked } as any
    })
  }, [org, uid, updateMutation])

  const handleDelete = useCallback(() => {
    deleteMutation.mutate({
      params: { path: { orgCode: org, derivationUid: uid } }
    })
  }, [org, uid, deleteMutation])

  if (isLoading) {
    return (
      <div className="flex justify-center py-16">
        <AppIcon path={mdiLoading} size="xl" className="motion-safe:animate-spin" />
      </div>
    )
  }

  if (error || !d) {
    return (
      <div className="container mx-auto py-8 max-w-4xl">
        <div className="text-destructive text-center py-8">
          {t('not_found')}
        </div>
      </div>
    )
  }

  return (
    <div className="container mx-auto py-8 max-w-4xl">
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-foreground">{d.name?.en || d.code}</h1>
          <code className="text-sm text-muted-foreground">{d.code}</code>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            {/* The toggle doubles as the enabled indicator, so it stays on
                screen (read-only) rather than disappearing. */}
            <Checkbox
              checked={enabled}
              disabled={!canWriteConfiguration}
              onCheckedChange={(checked) => handleToggleEnabled(checked === true)}
              data-testid="derivation-enabled-toggle"
            />
            <Label className="text-sm">{t('detail.enabled_label')}</Label>
          </div>
          {canWriteConfiguration && (
            <Button
              variant="destructive"
              size="icon"
              onClick={() => setShowDeleteDialog(true)}
              data-testid="delete-derivation-btn"
            >
              <AppIcon path={mdiTrashCanOutline} size="sm" />
            </Button>
          )}
        </div>
      </div>

      {/* Summary badges */}
      <div className="flex gap-2 mb-6 ml-10">
        <Badge variant="secondary" className="gap-1.5">
          {t('badges.target')} <code className="text-xs">{d.target_property?.code || targetPropertyCode}</code>
        </Badge>
        <Badge variant="secondary" className="gap-1.5">
          {t('badges.inputs_count', { count: d.inputs?.length || 0 })}
        </Badge>
        <Badge variant="secondary" className="gap-1.5" data-testid="derivation-kind-badge">
          {isTable
            ? <><AppIcon path={mdiFileTree} size="xs" /> {t('badges.decision_table')} · {t('badges.rules_count', { count: (d.decision_table || []).length })}</>
            : <><AppIcon path={mdiCodeTags} size="xs" /> {t('badges.script')}</>}
        </Badge>
        {(d.asset_types || []).length > 0 && (
          <Badge variant="secondary" className="gap-1.5" data-testid="derivation-scope-badge">
            {t('badges.scope')} {(d.asset_types as string[]).map(assetTypeLabel).join(', ')}
          </Badge>
        )}
      </div>

      {/* Last evaluation failure (spec 2026-10-06-12): runtime state, reset
          when the derivation is saved. */}
      {d.last_error && (
        <Alert variant="destructive" className="mb-6" data-testid="derivation-last-error">
          <AppIcon path={mdiAlert} size="md" />
          <AlertTitle>{t('last_error.title')}</AlertTitle>
          <AlertDescription className="space-y-1">
            <pre className="whitespace-pre-wrap break-words font-mono text-xs" data-testid="derivation-last-error-message">{d.last_error}</pre>
            <p className="text-xs">
              {d.last_error_at && t('last_error.at', { date: formatDate(d.last_error_at, { dateStyle: 'short', timeStyle: 'short' }) })}
              {d.last_error_asset_uid && (
                <>
                  {' '}
                  <Link
                    to="/$org/assets/$assetUid"
                    params={{ org, assetUid: d.last_error_asset_uid }}
                    className="underline font-medium"
                    data-testid="derivation-last-error-asset-link"
                  >
                    {t('last_error.asset_link')}
                  </Link>
                </>
              )}
            </p>
            <p className="text-xs text-muted-foreground">{t('last_error.hint')}</p>
          </AlertDescription>
        </Alert>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-6">
          <TabsTrigger value="overview" data-testid="tab-overview" className="gap-1.5">
            <AppIcon path={mdiPound} size="xs" />
            {t('detail.tabs.overview')}
          </TabsTrigger>
          {isTable && (
            <TabsTrigger value="rules" data-testid="tab-rules" className="gap-1.5">
              <AppIcon path={mdiFileTree} size="xs" />
              {t('detail.tabs.rules')}
            </TabsTrigger>
          )}
          <TabsTrigger value="test" data-testid="tab-test" className="gap-1.5">
            <AppIcon path={mdiFlaskOutline} size="xs" />
            {t('detail.tabs.test')}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          <div className="space-y-6">
            {/* Identity */}
            <div className="bg-card rounded-lg border border-border p-5">
              <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-4">{t('detail.identity_section')}</h3>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="edit-code">{t('detail.code_label')}</Label>
                  <Input
                    id="edit-code"
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    className="font-mono"
                    readOnly={!canWriteConfiguration}
                    data-testid="edit-code"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="edit-name-en">{t('detail.name_en_label')}</Label>
                    <Input
                      id="edit-name-en"
                      value={nameEn}
                      onChange={(e) => setNameEn(e.target.value)}
                      readOnly={!canWriteConfiguration}
                      data-testid="edit-name-en"
                    />
                  </div>
                  <div>
                    <Label htmlFor="edit-name-fr">{t('detail.name_fr_label')}</Label>
                    <Input
                      id="edit-name-fr"
                      value={nameFr}
                      onChange={(e) => setNameFr(e.target.value)}
                      readOnly={!canWriteConfiguration}
                      data-testid="edit-name-fr"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="edit-description">{t('detail.description_label')}</Label>
                  <Textarea
                    id="edit-description"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    rows={3}
                    readOnly={!canWriteConfiguration}
                    data-testid="edit-description"
                  />
                </div>
              </div>
            </div>

            {/* Configuration */}
            <div className="bg-card rounded-lg border border-border p-5">
              <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-4">{t('detail.configuration_section')}</h3>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="edit-target">{t('detail.target_property_label')}</Label>
                  <Input
                    id="edit-target"
                    value={targetPropertyCode}
                    onChange={(e) => setTargetPropertyCode(e.target.value)}
                    className="font-mono"
                    readOnly={!canWriteConfiguration}
                    data-testid="edit-target-property"
                  />
                  <p className="text-xs text-muted-foreground mt-1">{t('detail.target_property_help')}</p>
                </div>
                <div>
                  <Label>{t('detail.asset_types_label')}</Label>
                  <div className="flex flex-wrap gap-1.5 mt-1.5" data-testid="derivation-asset-types">
                    {assetTypeCodes.length > 0 ? (
                      assetTypeCodes.map((atCode) => (
                        <Badge
                          key={atCode}
                          variant="secondary"
                          className="gap-1 pr-1"
                          data-testid={`derivation-asset-type-${atCode}`}
                        >
                          <span>{assetTypeLabel(atCode)}</span>
                          <code className="text-xs text-muted-foreground">{atCode}</code>
                          {canWriteConfiguration && (
                            <button
                              type="button"
                              className="rounded-sm hover:bg-muted p-0.5"
                              aria-label={t('detail.asset_types_remove', { code: atCode })}
                              title={t('detail.asset_types_remove', { code: atCode })}
                              onClick={() => setAssetTypeCodes(assetTypeCodes.filter((c) => c !== atCode))}
                              data-testid={`derivation-asset-type-remove-${atCode}`}
                            >
                              <AppIcon path={mdiClose} size="xs" />
                            </button>
                          )}
                        </Badge>
                      ))
                    ) : (
                      <span className="text-sm text-muted-foreground">{t('detail.asset_types_all')}</span>
                    )}
                  </div>
                  {canWriteConfiguration && (
                    <Select
                      value=""
                      onValueChange={(v) => {
                        if (v && !assetTypeCodes.includes(v)) setAssetTypeCodes([...assetTypeCodes, v])
                      }}
                    >
                      <SelectTrigger className="mt-2" data-testid="derivation-asset-types-add">
                        <SelectValue placeholder={t('detail.asset_types_add_placeholder')} />
                      </SelectTrigger>
                      <SelectContent>
                        {assetTypes
                          .filter((at: any) => !assetTypeCodes.includes(at.code))
                          .map((at: any) => (
                            <SelectItem key={at.uid} value={at.code}>
                              <span className="flex items-center gap-2">
                            <AssetTypeTile type={at as AssetTypeLike} size="sm" />
                            <span>{typeof at.name === 'string' ? at.name : at.name?.en || at.name?.fr || at.code}</span>
                          </span>
                              <code className="ml-2 text-xs text-muted-foreground">{at.code}</code>
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                  )}
                  <p className="text-xs text-muted-foreground mt-1">{t('detail.asset_types_help')}</p>
                </div>
                {isTable ? (
                  <div>
                    <Label>{t('detail.table_inputs_label')}</Label>
                    <div className="flex flex-wrap gap-1.5 mt-1.5" data-testid="derivation-table-inputs">
                      {(d.inputs || []).map((path: string) => (
                        <Badge key={path} variant="secondary" className="font-mono text-xs">{path}</Badge>
                      ))}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">{t('detail.table_inputs_help')}</p>
                  </div>
                ) : (
                  <>
                    <DerivationInputsField
                      org={org}
                      value={inputs}
                      onChange={setInputs}
                      assetTypes={assetTypes}
                      readOnly={!canWriteConfiguration}
                    />
                    {undeclaredInputs.filter(p => !inputs.includes(p)).length > 0 && (
                      <div className="flex items-start gap-2 rounded-md border border-warning/40 bg-warning/10 px-3 py-2 text-xs" data-testid="derivation-undeclared-inputs-warning">
                        <AppIcon path={mdiAlert} size="xs" className="mt-0.5 flex-shrink-0 text-warning" />
                        <span>{t('detail.undeclared_inputs_warning', { paths: undeclaredInputs.filter(p => !inputs.includes(p)).join(', ') })}</span>
                      </div>
                    )}
                    {proposedInputs.length > 0 && canWriteConfiguration && (
                      <div className="rounded-md border border-primary/30 bg-primary/5 px-3 py-2 text-xs space-y-2" data-testid="derivation-proposed-inputs">
                        <div>
                          <p className="font-medium">{t('detail.proposed_inputs_title')}</p>
                          <p className="text-muted-foreground">{t('detail.proposed_inputs_help')}</p>
                        </div>
                        <div className="flex flex-wrap items-center gap-1.5">
                          {proposedInputs.map((path) => (
                            <Button
                              key={path}
                              type="button"
                              variant="outline"
                              size="sm"
                              className="h-7 font-mono text-xs"
                              aria-label={t('detail.proposed_inputs_add', { path })}
                              title={t('detail.proposed_inputs_add', { path })}
                              onClick={() => acceptProposedInputs([path])}
                              disabled={updateMutation.isPending}
                              data-testid={`derivation-proposed-input-${path}`}
                            >
                              <AppIcon path={mdiPlus} size="xs" className="mr-1" />
                              {path}
                            </Button>
                          ))}
                          <Button
                            type="button"
                            size="sm"
                            className="h-7"
                            onClick={() => acceptProposedInputs(proposedInputs)}
                            disabled={updateMutation.isPending}
                            data-testid="derivation-proposed-inputs-add-all"
                          >
                            {t('detail.proposed_inputs_add_all')}
                          </Button>
                        </div>
                      </div>
                    )}
                    <div>
                      <Label>{t('detail.script_label')}</Label>
                      <p className="text-xs text-muted-foreground mb-2">{t('detail.script_help')}</p>
                      <Suspense fallback={<div className="h-[300px] border border-input rounded-md" />}>
                        <CodeMirror
                          value={script}
                          onChange={(val: string) => setScript(val)}
                          height="300px"
                          maxHeight="500px"
                          placeholder={'const rent = ctx.prop("rent_annual") || 0;\nconst area = ctx.prop("area");\nif (!area) return null;\nreturn Math.round(rent / area * 100) / 100;'}
                          className="rounded-md border border-input overflow-hidden"
                          theme={theme === 'dark' ? 'dark' : 'light'}
                          editable={canWriteConfiguration}
                          completion={completion}
                          data-testid="edit-script"
                        />
                      </Suspense>
                      <p className="text-xs text-muted-foreground mt-1">
                        {script.length} / 4096
                      </p>
                      <Collapsible className="mt-2 rounded-md border border-border" data-testid="derivation-script-reference">
                        <CollapsibleTrigger asChild>
                          <button
                            type="button"
                            className="group flex w-full items-center gap-2 px-3 py-2 text-xs font-medium text-muted-foreground hover:text-foreground"
                            data-testid="derivation-script-reference-toggle"
                          >
                            <AppIcon path={mdiBookOpenVariant} size="xs" />
                            {t('detail.reference_title')}
                            <AppIcon path={mdiChevronDown} size="xs" className="ml-auto transition-transform group-data-[state=open]:rotate-180" />
                          </button>
                        </CollapsibleTrigger>
                        <CollapsibleContent>
                          <pre className="overflow-x-auto border-t border-border bg-muted/40 px-3 py-2 font-mono text-xs" data-testid="derivation-script-reference-content">{SCRIPT_API_REFERENCE}</pre>
                        </CollapsibleContent>
                      </Collapsible>
                    </div>
                  </>
                )}
              </div>
            </div>

            {saveErrorMessage && (
              <Alert variant="destructive" data-testid="derivation-save-error">
                <AppIcon path={mdiAlert} size="md" />
                <AlertTitle>{t('detail.save_error')}</AlertTitle>
                <AlertDescription>{saveErrorMessage}</AlertDescription>
              </Alert>
            )}

            {/* Meta + Save */}
            <div className="flex items-center justify-between">
              <div className="text-xs text-muted-foreground space-x-3">
                <span>UID: {d.uid}</span>
                <span>Created: {new Date(d.created_at).toLocaleDateString()}</span>
                <span>Updated: {new Date(d.updated_at).toLocaleDateString()}</span>
              </div>
              {canWriteConfiguration && (
                <Button
                  onClick={handleSave}
                  disabled={updateMutation.isPending}
                  data-testid="save-derivation-btn"
                >
                  {updateMutation.isPending ? <AppIcon path={mdiLoading} size="sm" className="mr-2 motion-safe:animate-spin" /> : <AppIcon path={mdiContentSave} size="sm" className="mr-2" />}
                  {t('detail.save_button')}
                </Button>
              )}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="rules">
          <DerivationRulesTable
            org={org}
            uid={uid}
            rules={d.decision_table || []}
            onRulesChange={() => invalidateDerivation(queryClient)}
          />
          {/* The script the table compiles to (spec 2026-10-06-13), read-only.
              Collapsed until the author asks for it: the rules are the model,
              the script is a by-product (issue #1481). */}
          <Collapsible className="mt-6 rounded-lg border border-border bg-card" data-testid="compiled-script-section">
            <div className="flex items-center justify-between gap-2 p-3">
              <CollapsibleTrigger asChild>
                <button
                  type="button"
                  className="group flex flex-1 items-center gap-2 text-left text-sm font-medium"
                  data-testid="compiled-script-toggle"
                >
                  <AppIcon path={mdiChevronDown} size="sm" className="-rotate-90 transition-transform group-data-[state=open]:rotate-0" />
                  {t('detail.compiled_script_label')}
                </button>
              </CollapsibleTrigger>
              {canWriteConfiguration && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowConvertDialog(true)}
                  data-testid="convert-to-script-btn"
                >
                  <AppIcon path={mdiSwapHorizontal} size="sm" className="mr-1" />
                  {t('detail.convert_to_script')}
                </Button>
              )}
            </div>
            <CollapsibleContent>
              <div className="border-t border-border p-5">
                <p className="text-xs text-muted-foreground mb-2">{t('detail.compiled_script_help')}</p>
                <Suspense fallback={<div className="h-[300px] border border-input rounded-md" />}>
                  <CodeMirror
                    value={d.script || ''}
                    height="300px"
                    className="rounded-md border border-input overflow-hidden"
                    theme={theme === 'dark' ? 'dark' : 'light'}
                    editable={false}
                    readOnly
                    data-testid="compiled-script"
                  />
                </Suspense>
              </div>
            </CollapsibleContent>
          </Collapsible>
        </TabsContent>

        <TabsContent value="test" className="space-y-6">
          <TryOnAssetPanel org={org} uid={uid} />
          <DerivationTestPanel
            org={org}
            uid={uid}
            inputs={d.inputs || []}
            testValues={d.test_values || []}
            onUndeclaredInputs={setUndeclaredInputs}
            onTestValuesChange={(values) => {
              updateMutation.mutate({
                params: { path: { orgCode: org, derivationUid: uid } },
                body: { test_values: values } as any
              })
            }}
          />
        </TabsContent>
      </Tabs>

      <AlertDialog open={showConvertDialog} onOpenChange={setShowConvertDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('detail.convert_dialog.title')}</AlertDialogTitle>
            <AlertDialogDescription>{t('detail.convert_dialog.description')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-convert">{t('detail.convert_dialog.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleConvert} data-testid="confirm-convert">
              {t('detail.convert_dialog.confirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('detail.delete_dialog.title')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('detail.delete_dialog.description', { name: d.name?.en || d.code })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete">{t('detail.delete_dialog.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="confirm-delete"
            >
              {t('detail.delete_dialog.confirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
